import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';

import { AvatarService } from '../../../../core/users/avatar';
import { CurrentUserService } from '../../../../services/current-user';
import { ProjectMemberRole } from '../../../../core/models/avatar-model';
import { PendingCheckContextStore } from './pending-check-context.store';
import { Checker12 } from '../../../../core/models/checker12';
import { Checker2 } from '../../../../core/models/checker2';

type ProjectMemberState = {
  members: ProjectMemberRole[];
  isLoading: boolean;
  error: string | null;
};

const initialState: ProjectMemberState = {
  members: [],
  isLoading: false,
  error: null,
};

export const ProjectMemberStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),


  withComputed((
    store,
    contextStore = inject(PendingCheckContextStore)
  ) => ({

    users: computed(() => store.members().map(m => m.user)),

    usersForChecker12: computed(() => {
      const checker11Oid = contextStore.checkInfoViewModel()?.checker11?.oid;
      return store.members()
        .filter(m =>
          (Object.values(Checker12) as string[]).includes(m.role) ||
          checker11Oid === m.user.oid
        )
        .map(m => m.user);
    }),

    usersForChecker2: computed(() =>
      store.members()
        .filter(m => (Object.values(Checker2) as string[]).includes(m.role))
        .map(m => m.user)
    )
  })),

  withComputed((
    store,
    currentUserService = inject(CurrentUserService)
  ) => ({

    usersForChecker12Consultant: computed(() => {
      const base = store.usersForChecker12();
      const me = currentUserService.currentUser();

      if (!me) return base;

      const alreadyIn = base.some(u => u.oid === me.oid);
      return alreadyIn ? base : [...base, me];
    })

  })),

  withMethods((
    store,
    avatarService = inject(AvatarService)
  ) => ({

    loadMembersByProjectCode: rxMethod<string | null>(
      pipe(
        tap(() => patchState(store, { isLoading: true, error: null })),
        switchMap((projectCode) => {
          if (!projectCode) {
            patchState(store, { members: [], isLoading: false });
            return of([]);
          }

          return avatarService.getProjectMembers(projectCode).pipe(
            tapResponse({
              next: (members) => patchState(store, { members, isLoading: false }),
              error: (err: any) => {
                patchState(store, { error: err.message, isLoading: false });
                console.error('Fehler beim Laden der Projektmitglieder:', err);
              }
            })
          );
        })
      )
    )
  }))
);
